import type { ButtonHTMLAttributes, ReactNode } from "react";
import { injectCss } from "./injectCss";

const CSS = `
.js-btn{
  --_bg:var(--brand); --_fg:var(--text-on-brand); --_bd:transparent;
  display:inline-flex;align-items:center;justify-content:center;gap:8px;
  font-family:var(--font-sans);font-weight:var(--weight-bold);
  border:1.5px solid var(--_bd);border-radius:var(--radius-md);
  background:var(--_bg);color:var(--_fg);cursor:pointer;
  white-space:nowrap;text-decoration:none;
  transition:background var(--dur-fast) var(--ease-out),
             box-shadow var(--dur-fast) var(--ease-out),
             transform var(--dur-fast) var(--ease-out),
             border-color var(--dur-fast) var(--ease-out);
}
.js-btn:focus-visible{outline:none;box-shadow:var(--ring-brand)}
.js-btn:active{transform:scale(.975)}
.js-btn[disabled]{opacity:.5;cursor:not-allowed;transform:none}
.js-btn--sm{height:var(--control-sm);padding:0 14px;font-size:var(--text-sm)}
.js-btn--md{height:var(--control-md);padding:0 18px;font-size:var(--text-body)}
.js-btn--lg{height:var(--control-lg);padding:0 24px;font-size:var(--text-h3)}
.js-btn--primary{--_bg:var(--brand);--_fg:#fff;box-shadow:var(--shadow-brand)}
.js-btn--primary:hover:not([disabled]){--_bg:var(--brand-hover)}
.js-btn--primary:active:not([disabled]){--_bg:var(--brand-active)}
.js-btn--accent{--_bg:var(--accent);--_fg:#fff}
.js-btn--accent:hover:not([disabled]){--_bg:var(--accent-hover)}
.js-btn--secondary{--_bg:var(--surface-card);--_fg:var(--text-body);--_bd:var(--border-default)}
.js-btn--secondary:hover:not([disabled]){--_bg:var(--neutral-50);--_bd:var(--border-strong)}
.js-btn--ghost{--_bg:transparent;--_fg:var(--brand);--_bd:transparent}
.js-btn--ghost:hover:not([disabled]){--_bg:var(--brand-subtle)}
.js-btn--danger{--_bg:var(--danger-bg);--_fg:var(--danger-fg);--_bd:transparent}
.js-btn--danger:hover:not([disabled]){--_bg:var(--red-500);--_fg:#fff}
.js-btn--block{width:100%}
`;

export type ButtonVariant = "primary" | "accent" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md" | "lg";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  block?: boolean;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
}

export function Button({
  variant = "primary",
  size = "md",
  block = false,
  iconLeft,
  iconRight,
  type = "button",
  className = "",
  children,
  ...rest
}: ButtonProps) {
  injectCss("js-btn-css", CSS);
  const cls = [
    "js-btn",
    `js-btn--${size}`,
    `js-btn--${variant}`,
    block ? "js-btn--block" : "",
    className,
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <button type={type} className={cls} {...rest}>
      {iconLeft}
      {children != null && <span>{children}</span>}
      {iconRight}
    </button>
  );
}
